/**
 * Persisted layout store: sidebar collapse, open menus per mode.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { SidebarConfig } from '@/types/preferences.types';
import { updateInterfaceConfig } from '@/services/preferences.service';
import type { AppMode } from '@/contexts/ModeContext';

/** Sidebar collapse + per-mode open menus (debounced persist to preferences). */
interface LayoutStore {
  isSidebarCollapsed: boolean;
  /** Open menu groups keyed by mode so each mode remembers its own expanded groups. */
  openMenusByMode: Record<AppMode, string[]>;
  /** 64px collapsed, 256px expanded */
  sidebarWidth: number;
  /** 56px */
  navbarHeight: number;
  saveTimer: ReturnType<typeof setTimeout> | null;
  /** Toggle collapse and debounce-persist sidebar prefs. */
  toggleSidebar: () => void;
  setSidebarCollapsed: (_collapsed: boolean) => void;
  /** Expand/collapse one menu group for the given mode only. */
  toggleMenu: (_menuId: string, _mode: AppMode) => void;
  setOpenMenus: (_menuIds: string[], _mode: AppMode) => void;
  /** Debounced PATCH of sidebar collapse + open menus. */
  saveSidebarState: () => void;
  /** Apply API collapse flag; open menus stay on localStorage. */
  loadSidebarState: (_config: SidebarConfig) => void;
}

/** Collapsed rail width (px). */
const COLLAPSED_WIDTH = 64;
/** Expanded sidebar width (px). */
const EXPANDED_WIDTH = 240;
/** Top navbar height (px) used for layout offset. */
const NAVBAR_HEIGHT = 56;
/** Debounce before PATCH-ing sidebar prefs so collapse/expand does not spam the API. */
const SAVE_DEBOUNCE_MS = 2000;

/** Layout store: sidebar width/collapse and open menu groups per {@link AppMode}. */
export const useLayoutStore = create<LayoutStore>()(
  persist(
    (set, get) => ({
      isSidebarCollapsed: true,
      openMenusByMode: { in_person: [], remote: [] },
      sidebarWidth: COLLAPSED_WIDTH,
      navbarHeight: NAVBAR_HEIGHT,
      saveTimer: null,

      toggleSidebar: () => {
        const newCollapsed = !get().isSidebarCollapsed;
        set({
          isSidebarCollapsed: newCollapsed,
          sidebarWidth: newCollapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH,
        });

        get().saveSidebarState();
      },

      setSidebarCollapsed: (collapsed: boolean) => {
        set({
          isSidebarCollapsed: collapsed,
          sidebarWidth: collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH,
        });

        get().saveSidebarState();
      },

      toggleMenu: (menuId: string, mode: AppMode) => {
        const { openMenusByMode } = get();
        const current = openMenusByMode[mode] ?? [];
        const isOpen = current.includes(menuId);
        const next = isOpen ? current.filter((id) => id !== menuId) : [...current, menuId];
        set({ openMenusByMode: { ...openMenusByMode, [mode]: next } });
      },

      setOpenMenus: (menuIds: string[], mode: AppMode) => {
        const { openMenusByMode } = get();
        set({ openMenusByMode: { ...openMenusByMode, [mode]: menuIds } });
      },

      saveSidebarState: () => {
        const state = get();

        if (state.saveTimer) {
          clearTimeout(state.saveTimer);
        }

        const timer = setTimeout(() => {
          const sidebarConfig: SidebarConfig = {
            isCollapsed: state.isSidebarCollapsed,
            openMenusByMode: state.openMenusByMode,
            lastUpdated: new Date().toISOString(),
          };
          void updateInterfaceConfig('sidebar', sidebarConfig as Record<string, unknown>).catch(
            () => {
              /* silent fail — layout persist must not block UI */
            },
          );
        }, SAVE_DEBOUNCE_MS);

        set({ saveTimer: timer });
      },

      loadSidebarState: (config: SidebarConfig) => {
        set({
          isSidebarCollapsed: config.isCollapsed ?? true,
          // Note: openMenus is NOT loaded from API, we keep localStorage state
          sidebarWidth: (config.isCollapsed ?? true) ? COLLAPSED_WIDTH : EXPANDED_WIDTH,
        });

      },
    }),
    {
      name: 'ednov-layout',
      partialize: (state) => ({
        isSidebarCollapsed: state.isSidebarCollapsed,
        openMenusByMode: state.openMenusByMode,
        sidebarWidth: state.sidebarWidth,
      }),
    },
  ),
);
